﻿
import { FC } from 'react';
import { Container, Section, Card, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { siteConfig } from '../config/site';
import { PageProps } from './HomePage';

export const RefundPolicyPage: FC<PageProps> = () => {
  return (
    <>
      <SEOHead
        title="Refund & Cancellation Policy — Connect Source"
        description="Review Connect Source digital product delivery, refund requests, payment issues, and coaching cancellation and rescheduling terms."
      />

      <Section
        style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid var(--color-border-subtle)',
          padding: '4rem 0'
        }}
      >
        <Container size="md">
          <Badge variant="neutral" style={{ marginBottom: '1rem' }}>
            Fulfillment & Terms
          </Badge>

          <h1
            style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: 'var(--color-navy-dark)',
              lineHeight: 1.2
            }}
          >
            Refund & Cancellation Policy
          </h1>

          <p
            style={{
              fontSize: '0.9rem',
              color: 'var(--color-text-muted)',
              marginTop: '0.5rem'
            }}
          >
            Last Updated: October 2026 • Connect Source
          </p>

          <Card
            style={{
              marginTop: '2.5rem',
              padding: '2.5rem',
              lineHeight: 1.75,
              color: 'var(--color-text-secondary)',
              fontSize: '0.95rem'
            }}
          >
            <h2>1. Digital Products & Delivery</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Connect Source sells downloadable digital products, including
              career toolkits, templates, guides, and other digital resources.
              After successful payment verification, download access is
              provided through the website and/or the email address supplied
              during checkout, as applicable to the product. Delivery is
              electronic; no physical shipment is included unless explicitly
              stated on a product page.
            </p>

            <h2>2. Refund Requests for Digital Products</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Because digital products can be accessed or downloaded
              electronically, purchases are generally considered final once
              download access has been issued. However, this policy does not
              exclude any refund, remedy, or other right that may apply under
              applicable law. If a product was incorrectly described, access
              cannot be provided, or another purchase issue arises, please
              contact us so we can review the circumstances and determine an
              appropriate resolution.
            </p>

            <h2>3. Failed, Pending, or Duplicate Payments</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              If your account is debited but your order is not confirmed, or
              you believe you have been charged more than once for the same
              purchase, please contact us with your order reference, payment
              date, amount, and available transaction reference. Do not send
              full card numbers, banking passwords, PINs, or OTPs. We will
              review the transaction and, where a refund is appropriate,
              initiate or facilitate the applicable refund process. The time
              taken for funds to appear in your account may depend on the
              payment provider and your bank.
            </p>

            <h2>4. Download or Technical Access Problems</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              If a download link does not work, a file appears corrupted, or
              you cannot access a purchased product, email us at{' '}
              <a
                href={`mailto:${siteConfig.contactEmail}`}
                style={{ color: 'var(--color-brand-primary)' }}
              >
                {siteConfig.contactEmail}
              </a>{' '}
              with your order reference and a description of the issue. We
              will investigate and, where possible, restore access, provide a
              replacement link, or resend the deliverable. Please allow up to
              24 hours for our initial resolution attempt.
            </p>

            <h2>5. Career Coaching: Cancellation & Rescheduling</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              If you need to reschedule a booked one-to-one coaching session,
              please provide at least 24 hours' advance notice. We will work
              with you to arrange another mutually suitable time. Missed
              sessions or late cancellations may be treated as forfeited where
              the applicable booking terms permit. Refund requests for
              coaching services will be reviewed according to the service
              purchased, the circumstances, and applicable law.
            </p>

            <h2>6. How to Contact Us</h2>
            <p>
              For refund, payment, cancellation, or delivery enquiries, contact
              Connect Source at{' '}
              <a
                href={`mailto:${siteConfig.contactEmail}`}
                style={{ color: 'var(--color-brand-primary)' }}
              >
                {siteConfig.contactEmail}
              </a>{' '}
              or WhatsApp us at {siteConfig.supportPhone}. Please include your
              order reference where available so we can investigate efficiently.
            </p>
          </Card>
        </Container>
      </Section>
    </>
  );
};
